/**
 * @name: Product frequently bought together
 * @description: Starts the existing cart upsell slider on the product page and lets it meet the right edge of the screen.
 * @dependencies: snippets/cart-upsell-products.liquid, assets/swiper-bundle.min.js
 * @strict-mode: true
 */

/**
 * @extends {HTMLElement}
 */
class ProductFrequentlyBoughtTogether extends HTMLElement {
  /** @type {number} */
  #edgeFrame = 0;

  /** @type {(() => void) | null} */
  #onResize = null;

  /** @type {(() => void) | null} */
  #onSliderReady = null;

  connectedCallback() {
    this.#onResize = () => this.#queueEdgeSync();
    this.#syncEdge();
    this.#initSlider();
    window.addEventListener('resize', this.#onResize);
  }

  disconnectedCallback() {
    if (this.#onSliderReady) {
      document.removeEventListener('theme:cart-upsell-ready', this.#onSliderReady);
      this.#onSliderReady = null;
    }

    window.removeEventListener('resize', this.#onResize);

    if (this.#edgeFrame) {
      window.cancelAnimationFrame(this.#edgeFrame);
      this.#edgeFrame = 0;
    }

    window.theme?.destroyCartUpsellSlider?.(this);
  }

  #queueEdgeSync() {
    if (document.querySelector('#QuickAddModalProductCards.modal--is-active')) return;
    if (this.#edgeFrame) return;

    this.#edgeFrame = window.requestAnimationFrame(() => {
      this.#edgeFrame = 0;
      this.#syncEdge();
    });
  }

  /**
   * Extends the slider to the right edge of the layout, the same way the cart drawer cancels its padding.
   */
  #syncEdge() {
    const slider = this.querySelector('[data-cart-upsell-slider]');
    const header = this.querySelector('.cart-upsell__header');

    if (!(slider instanceof HTMLElement) || !(header instanceof HTMLElement)) return;

    const edge = document.documentElement.clientWidth;
    const right = header.getBoundingClientRect().right;
    const bleed = Math.max(0, Math.floor(edge - right));

    slider.style.marginRight = bleed > 0 ? `-${bleed}px` : '0px';
    slider.cartUpsellSwiper?.update();
  }

  #initSlider() {
    const initSlider = window.theme?.initCartUpsellSlider;

    if (typeof initSlider !== 'function') {
      this.#onSliderReady = () => {
        this.#onSliderReady = null;
        this.#initSlider();
      };
      document.addEventListener('theme:cart-upsell-ready', this.#onSliderReady, { once: true });
      return;
    }

    this.#syncEdge();
    initSlider(this);
  }
}

if (!customElements.get('product-frequently-bought-together')) {
  customElements.define('product-frequently-bought-together', ProductFrequentlyBoughtTogether);
}
